import { Manifestation, Work } from "@biblioteksentralen/cordata";
import { Box, Heading, Stack, VStack } from "@biblioteksentralen/react";
import { KulturfondVideo } from "@libry-content/types";
import { sort, unique } from "radash";
import { useRef } from "react";
import useSWR from "swr";
import { DefaultContainer } from "../../../components/ContentContainer";
import AppealTerms from "../../../components/forrigeBok/AppealTerms";
import ReadAlikes from "../../../components/forrigeBok/ReadAlikes";
import { useForrigeBokWork } from "../../../components/forrigeBok/useForrigeBokData";
import { useCommonData } from "../../../components/layout/CommonDataProvider";
import { useTranslation } from "../../../utils/hooks/useTranslation";
import {
  getAggregateManifestationWorksCacheKey,
  getWorksAssociatedWithAggregateManifestations,
} from "../../cordata/aggregateManifestations";
import { getDocumentTypeDefiniteNoun } from "../../cordata/documentTypes";
import { getSortableManifestation, markAsAggregate } from "../../cordata/manifestations";
import { LibraryInfo } from "../../sanityQueries";
import { getSearchConfig } from "../../searchApi/searchConfig";
import { SubjectLinks } from "../SubjectLinks";
import ManifestationCard from "../manifestations/ManifestationCard";
import { CollapsableWithGradient } from "./CollapsableWithGradient";
import { KulturfondVideoView } from "./KulturfondVideoView";
import RecommendationCardForWorkView from "./RecommendationCardForWorkView";
import { WorkViewBanner } from "./WorkViewBanner/WorkViewBanner";
import { useWorkViewData } from "./WorkViewBanner/useWorkViewData";
import { useHoldingsData } from "../../hooks/useHoldingsData";
import { supportedExternalContentServices } from "../reservation/externalContentService";
import { CordataWork, getSortedLanguagesListIndex } from "@libry-content/integrations";
import { WorkPageRecommendation } from "../../../app/[locale]/sites/[siteDomain]/samling/verk/[workId]/fetchRecommendationData";

//used to filter out expressions and manifestations where every item in holdings has an invalid state
const useWorkPrunedByHoldings = (unfilteredWork: Work) => {
  const manifestationIds = unique(
    unfilteredWork.expressions?.flatMap(({ manifestations = [], aggregateManifestations = [] }) =>
      [...manifestations, ...aggregateManifestations].map(({ id }) => id)
    ) ?? []
  );

  const { holdingsData, hasError, isLoading: isLoadingHoldings } = useHoldingsData(unfilteredWork, manifestationIds);

  //used to check if a manifestation has at least one item with valid holding state.
  //holding states can be seen here: https://bs-redia.atlassian.net/l/cp/GvJidqrC
  const manifestationHoldingStatusIsValid = ({ libraryCatalogueRecords = [], _origin }: Manifestation) => {
    if (
      supportedExternalContentServices.find(
        (externalContentService) => externalContentService.originName === _origin?.name
      )
    )
      return true;
    return libraryCatalogueRecords.some(({ localRecordId }) => {
      const holdingsDataForRecord = holdingsData?.[localRecordId];
      if (!holdingsDataForRecord) return false;
      const { reserveId } = holdingsDataForRecord;
      if (
        holdingsDataForRecord.locations.every((location) =>
          //checking if every holding state is invalid
          location.items.every(
            ({ loanableState, availableState }) =>
              (availableState === "available" && loanableState === "not_loanable" && !!reserveId) || //invalid state
              (availableState === "not_available" && loanableState === "not_loanable" && !reserveId) //lost item, deemed invalid state
          )
        )
      )
        return false;
      return true;
    });
  };

  if (hasError) return { hasHoldingsDataError: true };

  if (isLoadingHoldings) return {};

  return {
    work: {
      ...unfilteredWork,
      expressions: (unfilteredWork.expressions ?? [])
        .map(({ manifestations = [], aggregateManifestations = [], ...rest }) => ({
          ...rest,
          manifestations: manifestations.filter(manifestationHoldingStatusIsValid),
          aggregateManifestations: aggregateManifestations.filter(manifestationHoldingStatusIsValid),
        }))
        .filter(
          ({ manifestations = [], aggregateManifestations = [] }) =>
            !!manifestations.length || !!aggregateManifestations.length
        ),
    },
  };
};

type WorkViewProps = {
  work: Work;
  recommendation: WorkPageRecommendation | null;
  libraries: LibraryInfo[];
  kulturFondVideo?: KulturfondVideo;
};

export const WorkView = ({ work: unprunedWork, recommendation, libraries, kulturFondVideo }: WorkViewProps) => {
  const { t, ts } = useTranslation();
  const manifestationsTitleRef = useRef<null | HTMLAnchorElement>(null);
  const { site } = useCommonData();

  const searchConfig = getSearchConfig(site);
  if (!searchConfig.isSearchIntegrationEnabled) {
    console.error("Search integration not enabled for this site");
  }

  const { work: workPrunedByHoldings, hasHoldingsDataError } = useWorkPrunedByHoldings(unprunedWork);
  const hasWorkHoldings = !workPrunedByHoldings || !!workPrunedByHoldings.expressions?.length;
  const isHoldingsDataUpdated = !!workPrunedByHoldings || !hasWorkHoldings || hasHoldingsDataError;
  // Use unfiltered work if the library has no holdings or if the updated data from the holdings API is not yet ready.
  // This means that there's a chance the displayed frontend data changes once the updated holdings are obtained, when
  // theholdings according to the data from the search backend deviates from the updated data. If this becomes an
  // issue we could show loaders until the updated data is available. For now we use a blurred version of the image
  // until the data is ready, assuming that a changing image is more confusing than text.
  const work = hasWorkHoldings && !!workPrunedByHoldings ? workPrunedByHoldings : unprunedWork;
  const forrigeBokData = useForrigeBokWork({ workId: work.id });

  const { currentDocumentType, representativeManifestation } = useWorkViewData(work);

  const aggregateWorkTriples = new CordataWork(work)
    .getWorkTriples()
    .filter(({ isAggregateManifestation }) => isAggregateManifestation);

  const { data: associatedWithAggregateManifestations, error } = useSWR(
    getAggregateManifestationWorksCacheKey(aggregateWorkTriples, site),
    () => getWorksAssociatedWithAggregateManifestations(aggregateWorkTriples, searchConfig)
  );

  if (error) console.error(error);

  return (
    <Box margin={{ base: "1rem 0 6rem", md: "2rem 0 8rem" }}>
      {/* We keep the banner outside of the container, since part of it is supposed to cover entire width. */}
      <WorkViewBanner
        work={work}
        representativeManifestation={representativeManifestation}
        coverImageQuality={isHoldingsDataUpdated ? "original" : "blur"}
        libraries={libraries}
        documentType={currentDocumentType}
        hasWorkHoldings={hasWorkHoldings}
        hasHoldingsDataError={hasHoldingsDataError}
        manifestationsTitleRef={manifestationsTitleRef}
      />
      <DefaultContainer marginTop="2rem">
        <Stack padding={{ sm: "1rem", lg: 0 }} spacing="3rem">
          {recommendation && (
            <Box>
              <Heading as="h2" fontSize="xl">
                {recommendation.isLocal ? t("Anbefaling fra biblioteket") : t("Anbefaling fra et annet bibliotek")}
              </Heading>
              <RecommendationCardForWorkView recommendation={recommendation} />
            </Box>
          )}

          {(work.summary || work.subjects.length > 0) && (
            <Box>
              <Heading as="h2" fontSize="xl" marginBottom="1rem">
                {t("Om")} {ts(getDocumentTypeDefiniteNoun(currentDocumentType))}
              </Heading>
              {work.subjects.length > 0 && <SubjectLinks subjects={work.subjects} marginBottom="1rem" />}
              {work.summary && (
                <CollapsableWithGradient
                  maxWidth="30rem"
                  readLessText={t("Les mindre")}
                  readMoreText={t("Les hele beskrivelsen")}
                  isOverflowingGuess={work.summary.length > 150}
                >
                  {work.summary}
                </CollapsableWithGradient>
              )}
            </Box>
          )}
          <KulturfondVideoView kulturFondVideo={kulturFondVideo} />
          {forrigeBokData && (
            <Box>
              <Heading as="h2" fontSize="xl">
                {t("Leseopplevelse")}
              </Heading>
              <Box marginTop="1rem">
                <AppealTerms appealTerms={forrigeBokData.appealTerms} />
              </Box>
            </Box>
          )}
          <ReadAlikes workId={work.id} />
        </Stack>

        <Heading as="h2" fontSize="xl" margin="2rem 0 1rem 0 !important">
          {t("Utgaver")}
        </Heading>

        {hasHoldingsDataError && t("Noe gikk galt ved henting av beholdningsstatus.")}

        {!hasHoldingsDataError && (
          <VStack spacing="1rem" margin="0 1rem">
            {sort(work.expressions ?? [], ({ languages }) => getSortedLanguagesListIndex(languages)).flatMap(
              ({ manifestations = [], aggregateManifestations = [], ...rest }, idx) =>
                [
                  ...manifestations,
                  ...aggregateManifestations.map((aggregateManifestation) =>
                    markAsAggregate(aggregateManifestation, true)
                  ),
                ].map((manifestation) => {
                  const associatedWorks = associatedWithAggregateManifestations?.[manifestation.id];
                  return (
                    <ManifestationCard
                      key={manifestation.id}
                      work={work}
                      manifestation={getSortableManifestation(manifestation, rest)}
                      libraries={libraries}
                      associatedWorks={associatedWorks}
                      ref={idx === 0 ? manifestationsTitleRef : undefined}
                    />
                  );
                })
            )}
          </VStack>
        )}
      </DefaultContainer>
    </Box>
  );
};
